import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { Button, Menu } from '@headlessui/react';
import {
  EllipsisVerticalIcon,
  CurrencyRupeeIcon,
  ArrowPathIcon,
  XMarkIcon,
  ChevronLeftIcon
} from '@heroicons/react/24/outline';
import {
  EyeIcon
} from '@heroicons/react/24/outline';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDeviceInfo } from '../utils/deviceInfo';
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL;

const ProviderBalance = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState({});
  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    data: null,
    providerName: ''
  });
  const navigate = useNavigate();
  const [access_data, setAccess_data] = useState([]);

  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 20
  });

  useEffect(() => {
    if (sessionCheck() == null) {
      navigate("../login", { replace: true });
    } else {
      setAccess_data(JSON.parse(sessionCheck()));
    }
  }, []);

  useEffect(() => {
    if (access_data?.username) {
      fetchProviders();
    }
  }, [access_data?.username]);

  const fetchProviders = async () => {
    setLoading(true);

    try {
      const dataToEncrypt = {
        user_id: access_data?.username || '',
        login_id: access_data?.login_id || '',
        device_id: getDeviceInfo().deviceId,
        payload: {
          query: 'fetch-provider',
          category: '1',
        }
      };

      const encryptedMessage = encryptDataHybrid(dataToEncrypt);

      if (!encryptedMessage) {
        toast.error("Encryption failed. Please try again.");
        setLoading(false);
        return;
      }

      const payloadToSend = {
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      };

      const response = await fetch(`${API_URL}provider-operation.php`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
        },
        body: JSON.stringify(payloadToSend),
      });

      const result = await response.json();

      if (result.status === true) {
        const providersWithBalance = result.data.map(provider => ({
          ...provider,
          balance: null,
          apiResponse: null // Initialize apiResponse as null
        }));
        setProviders(providersWithBalance);

        // Update pagination
        setPagination(prev => ({
          ...prev,
          currentPage: 1,
          totalItems: result.data.length,
          totalPages: Math.ceil(result.data.length / prev.itemsPerPage)
        }));

        // Fetch balances for all providers
        fetchBalancesForProviders(providersWithBalance);
      } else {
        toast.error(result.message || "Failed to fetch providers data.");
      }
    } catch (error) {
      console.error("Fetch providers error:", error);
      toast.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchBalancesForProviders = async (providersList) => {
    // Initialize loading states for each provider
    const initialLoadingStates = {};
    providersList.forEach(provider => {
      initialLoadingStates[provider.company_id] = true;
    });
    setBalanceLoading(initialLoadingStates);

    // Fetch balance for each provider
    for (const provider of providersList) {
      try {
        const { balance, apiResponse } = await fetchProviderBalance(provider.company_id);

        setProviders(prevProviders =>
          prevProviders.map(p =>
            p.company_id === provider.company_id
              ? { ...p, balance, apiResponse }
              : p
          )
        );
      } catch (error) {
        console.error(`Failed to fetch balance for provider ${provider.company_id}:`, error);
        toast.error(`Failed to fetch balance for ${provider.company_name}`);
      } finally {
        setBalanceLoading(prev => ({
          ...prev,
          [provider.company_id]: false
        }));
      }
    }
  };

  const fetchProviderBalance = async (providerId) => {
    const dataToEncrypt = {
      user_id: access_data?.username || '',
      login_id: access_data?.login_id || '',
      device_id: getDeviceInfo().deviceId,
      payload: {
        query: 'get-provider-balance',
        provider_id: providerId
      }
    };

    const encryptedMessage = encryptDataHybrid(dataToEncrypt);

    if (!encryptedMessage) {
      throw new Error("Encryption failed");
    }

    const response = await fetch(`${API_URL}provider-operation.php`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
      },
      body: JSON.stringify({
        encrypted_key: encryptedMessage.encryptedKey,
        encrypted_data: encryptedMessage.encryptedData,
      }),
    });

    const result = await response.json();

    if (result.status !== true) {
      throw new Error(result.message || "Failed to fetch balance");
    }

    return {
      balance: result.balance || 0,
      apiResponse: result.api_response ? JSON.parse(result.api_response) : null
    };
  };

  const showResponseModal = (provider) => {
    setResponseModal({
      isOpen: true,
      data: provider.apiResponse,
      providerName: provider.company_name
    });
  };

  const closeResponseModal = () => {
    setResponseModal({
      isOpen: false,
      data: null,
      providerName: ''
    });
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
    }
  };

  const getPaginatedData = () => {
    const startIndex = (pagination.currentPage - 1) * 20;
    const endIndex = startIndex + 20;
    return providers.slice(startIndex, endIndex);
  };

  const toggleMobileSidebar = () => {
    setMobileSidebarOpen(!mobileSidebarOpen);
  };

  const refreshData = () => {
    fetchProviders();
  };

  return (

    <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
      <div className="flex flex-col flex-1">
        <Header />
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
              >
                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                Back
              </button>
              <h2 className="text-xl font-semibold">
                API Balance
              </h2>
              <button
                onClick={refreshData}
                className="px-3 py-2 text-sm text-gray-600 dark:text-gray-300 bg-gray-200 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 flex items-center"
              >
                <ArrowPathIcon className="h-4 w-4 mr-1" />
                Balance Refresh
              </button>
            </div>
            {children || (
              <>

                {/* Providers Table */}
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-x-auto mb-6">
                  {loading && (
                    <div className="flex justify-center items-center py-12">
                      <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
                    </div>
                  )}

                  {!loading && (
                    <>
                      <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider ID</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Provider Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                          {getPaginatedData().length > 0 ? (
                            getPaginatedData().map((provider) => (
                              <tr key={provider.company_id}>
                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">{provider.company_id}</td>
                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">{provider.company_name}</td>
                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">{provider.category}</td>
                                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-300">
                                  {balanceLoading[provider.company_id] ? (
                                    <div className="animate-pulse h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
                                  ) : (
                                    <span className="font-medium">
                                      ₹{provider.balance !== null ? provider.balance : 'N/A'}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                    ${provider.status === '1' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                      : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}>
                                    {provider.status === '1' ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-right text-sm font-medium">
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={() => showResponseModal(provider)}
                                      className={`px-2 py-1 text-xs rounded ${provider.apiResponse
                                        ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800'
                                        : 'bg-gray-100 text-gray-500 cursor-not-allowed dark:bg-gray-700 dark:text-gray-400'}`}
                                    >
                                      Show Response
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="px-4 py-4 text-center text-sm text-gray-500">
                                No providers found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination - Simplified */}
                      {providers.length > 20 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                          <div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                              Showing <span className="font-medium">{(pagination.currentPage - 1) * 20 + 1}</span> to{' '}
                              <span className="font-medium">
                                {Math.min(pagination.currentPage * 20, pagination.totalItems)}
                              </span>{' '}
                              of <span className="font-medium">{pagination.totalItems}</span> providers
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handlePageChange(pagination.currentPage - 1)}
                              disabled={pagination.currentPage === 1}
                              className="px-3 py-1 border border-gray-300 rounded text-sm bg-white dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                            >
                              Previous
                            </button>
                            <button
                              onClick={() => handlePageChange(pagination.currentPage + 1)}
                              disabled={pagination.currentPage === pagination.totalPages}
                              className="px-3 py-1 border border-gray-300 rounded text-sm bg-white dark:bg-gray-700 dark:border-gray-600 disabled:opacity-50"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
      {/* Response Modal */}
      <AnimatePresence>
        {responseModal.isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
            onClick={closeResponseModal}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    API Response for {responseModal.providerName}
                  </h3>
                  <button
                    onClick={closeResponseModal}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                  <pre className="text-sm text-gray-800 dark:text-gray-200 overflow-auto">
                    {JSON.stringify(responseModal.data, null, 2)}
                  </pre>
                </div>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={closeResponseModal}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer />
    </div>
  );
};

export default ProviderBalance;